import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import api, { API } from "@/lib/api";

/**
 * Public-site branding + theme (Phase 1 white-label foundation).
 *
 * Fetches the single global `public_site_settings` config and:
 *   · exposes brand/logos/social/seo via context (Logo + footer consume it),
 *   · injects admin-configured theme tokens as scoped CSS variables that apply
 *     ONLY on public routes (html[data-fl-site]) in the primary dark theme —
 *     so patient/clinician/admin dashboards and light mode are never affected,
 *   · sets favicon + document title + share meta from the config.
 *
 * Empty config → nothing is injected → the compiled Functional LAB defaults
 * render exactly as before.
 */

const DEFAULT = {
  brand: { clinicName: "", tagline: "", logoUrl: "", logoLightUrl: "", logoMarkUrl: "", faviconUrl: "", shareImageUrl: "" },
  theme: {}, themeLight: {}, typography: {}, social: {}, seo: {},
  nav: { items: [], signIn: { label: "", visible: true }, book: { label: "", path: "", visible: true } },
  pages: {},
  loaded: false,
  refresh: () => {},
};

// pathname → CMS page key (public routes only).
const PATH_TO_PAGE = [
  ["/team", "team"], ["/services", "services"], ["/prices", "prices"],
  ["/service-finder", "finder"], ["/resources", "resources"],
  ["/community", "community"], ["/testimonials", "testimonials"], ["/contact", "contact"],
];
function pageKeyForPath(pathname) {
  if (pathname === "/") return "home";
  for (const [prefix, key] of PATH_TO_PAGE) {
    if (pathname === prefix || pathname.startsWith(prefix + "/")) return key;
  }
  return null;
}

/**
 * Read a single public page's CMS overrides. Precedence is always
 * CMS value → caller-supplied i18n/default fallback, so an empty CMS field
 * never blanks the page.
 */
export function usePageCms(routeKey) {
  const { pages } = useSiteSettings();
  const page = pages?.[routeKey] || {};
  const byKey = {};
  (page.sections || []).forEach((s) => { if (s.key) byKey[s.key] = s; });
  const get = (sectionKey) => byKey[sectionKey] || {};
  const isVisible = (sectionKey) => byKey[sectionKey]?.visible !== false;
  const orderOf = (sectionKey, def) => (Number.isFinite(byKey[sectionKey]?.order) ? byKey[sectionKey].order : def);
  const text = (sectionKey, field, fallback) => {
    const v = byKey[sectionKey]?.[field];
    return v && String(v).trim() ? v : fallback;
  };
  // Per-item overrides merged by index over caller-supplied defaults.
  const items = (sectionKey, defaults = []) => {
    const stored = byKey[sectionKey]?.items || [];
    return defaults.map((d, i) => {
      const s = stored[i] || {};
      const pick = (f) => (s[f] && String(s[f]).trim() ? s[f] : d[f]);
      return {
        label: pick("label"),
        body: pick("body"),
        ctaLabel: pick("ctaLabel"),
        ctaPath: pick("ctaPath"),
        mediaUrl: pick("mediaUrl"),
        visible: s.visible === false ? false : d.visible !== false,
      };
    });
  };
  return { page, get, isVisible, orderOf, text, items };
}

function upsertMeta(attr, name, content) {
  if (!content) return;
  let m = document.querySelector(`meta[${attr}="${name}"]`);
  if (!m) { m = document.createElement("meta"); m.setAttribute(attr, name); document.head.appendChild(m); }
  m.setAttribute("content", content);
}

const FONT_LINKS = {
  "Inter": "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;800&display=swap",
  "Poppins": "https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;800&display=swap",
  "Montserrat": "https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;800&display=swap",
  "Roboto": "https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;700;900&display=swap",
  "Lora": "https://fonts.googleapis.com/css2?family=Lora:wght@400;500;600;700&display=swap",
  "Playfair Display": "https://fonts.googleapis.com/css2?family=Playfair+Display:wght@500;600;700&display=swap",
};

const SiteSettingsContext = createContext(DEFAULT);
export const useSiteSettings = () => useContext(SiteSettingsContext);

export function resolveAssetUrl(url) {
  if (!url) return "";
  if (/^https?:\/\//i.test(url)) return url;
  if (url.startsWith("/api/")) return `${API}${url.slice(4)}`; // API already ends with /api
  return url;
}

function isPublicPath(pathname) {
  return !(
    pathname.startsWith("/app") ||
    pathname.startsWith("/clinician") ||
    pathname.startsWith("/admin")
  );
}

function applyFontLink(font) {
  if (!font || !FONT_LINKS[font]) return;
  const id = `fl-font-${font.replace(/\s+/g, "-")}`;
  if (document.getElementById(id)) return;
  const l = document.createElement("link");
  l.id = id; l.rel = "stylesheet"; l.href = FONT_LINKS[font];
  document.head.appendChild(l);
}

function applyTheme(cfg) {
  const { theme = {}, themeLight = {}, typography = {} } = cfg;
  const toVars = (t) => {
    const v = [];
    if (t.primary) v.push(`--fl-orange:${t.primary}`);
    if (t.primaryHover) v.push(`--fl-orange-2:${t.primaryHover}`);
    if (t.background) v.push(`--fl-bg:${t.background}`);
    if (t.surface) v.push(`--fl-bg-2:${t.surface}`, `--fl-bg-3:${t.surface}`);
    if (t.text) v.push(`--fl-text:${t.text}`);
    if (t.textMuted) v.push(`--fl-muted:${t.textMuted}`);
    if (t.border) v.push(`--fl-border:${t.border}`);
    return v;
  };
  const darkVars = toVars(theme);
  const lightVars = toVars(themeLight);
  let css = "";
  if (darkVars.length) css += `html[data-fl-site]:not([data-theme="light"]){${darkVars.join(";")}}`;
  if (lightVars.length) css += `html[data-fl-site][data-theme="light"]{${lightVars.join(";")}}`;
  if (typography.bodyFont) { applyFontLink(typography.bodyFont); css += `html[data-fl-site]{font-family:'${typography.bodyFont}',system-ui,sans-serif}`; }
  if (typography.headingFont) { applyFontLink(typography.headingFont); css += `html[data-fl-site] .fl-heading{font-family:'${typography.headingFont}',system-ui,sans-serif}`; }
  if (typography.radius) css += `html[data-fl-site] .fl-card{border-radius:${typography.radius}}html[data-fl-site] .fl-btn,html[data-fl-site] .fl-btn-ghost{border-radius:${typography.radius}}`;

  let el = document.getElementById("fl-site-theme");
  if (!el) { el = document.createElement("style"); el.id = "fl-site-theme"; document.head.appendChild(el); }
  el.textContent = css;
}

function applyFavicon(url) {
  if (!url) return;
  let link = document.querySelector('link[rel="icon"]');
  if (!link) { link = document.createElement("link"); link.rel = "icon"; document.head.appendChild(link); }
  link.href = resolveAssetUrl(url);
}

export function SiteSettingsProvider({ children }) {
  const [cfg, setCfg] = useState(DEFAULT);
  const loc = useLocation();

  const refresh = useCallback(() => {
    api.get("/public/site-settings")
      .then((r) => setCfg({ ...DEFAULT, ...(r.data || {}), loaded: true }))
      .catch(() => setCfg((c) => ({ ...c, loaded: true })));
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Apply theme + favicon whenever the config changes.
  useEffect(() => {
    if (!cfg.loaded) return;
    applyTheme(cfg);
    applyFavicon(cfg.brand?.faviconUrl);
  }, [cfg]);

  // Toggle the public scope + apply SEO title/description on public routes only.
  useEffect(() => {
    const pub = isPublicPath(loc.pathname);
    const html = document.documentElement;
    if (pub) html.setAttribute("data-fl-site", "1");
    else html.removeAttribute("data-fl-site");

    if (pub && cfg.loaded) {
      const g = cfg.seo || {};
      const pageKey = pageKeyForPath(loc.pathname);
      const pageSeo = (pageKey && cfg.pages?.[pageKey]?.seo) || {};
      // Precedence: per-page override → global SEO → leave current default.
      const title = pageSeo.title || g.title;
      const desc = pageSeo.description || g.description;
      const shareTitle = pageSeo.shareTitle || g.shareTitle || title;
      const shareDesc = pageSeo.shareDescription || g.shareDescription || desc;
      const shareImg = resolveAssetUrl(pageSeo.shareImageUrl || cfg.brand?.shareImageUrl || "");
      if (title) document.title = title;
      if (desc) {
        let m = document.querySelector('meta[name="description"]');
        if (!m) { m = document.createElement("meta"); m.setAttribute("name", "description"); document.head.appendChild(m); }
        m.setAttribute("content", desc);
      }
      upsertMeta("property", "og:title", shareTitle);
      upsertMeta("property", "og:description", shareDesc);
      upsertMeta("property", "og:image", shareImg);
      upsertMeta("name", "twitter:title", shareTitle);
      upsertMeta("name", "twitter:description", shareDesc);
      upsertMeta("name", "twitter:image", shareImg);
    }
  }, [loc.pathname, cfg]);

  const value = { ...cfg, refresh };
  return <SiteSettingsContext.Provider value={value}>{children}</SiteSettingsContext.Provider>;
}
